import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Typography,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Group as UsersIcon,
  Email as EmailIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useDataProvider, useNotify, usePermissions } from 'react-admin';
import { PageTitle } from '../components/PageTitle';
import { tenantService } from '../services/tenantService';

interface TenantUser {
  user: {
    id: number;
    name: string;
    email: string;
    emailVerified: boolean;
    avatarUrl?: string;
  };
  roles: string[];
}

export const UsersPage = () => {
  const navigate = useNavigate();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const { permissions } = usePermissions();
  const [users, setUsers] = React.useState<TenantUser[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [searchQuery, setSearchQuery] = React.useState('');

  const isAdmin = permissions?.includes('admin');

  React.useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await dataProvider.getList('tenant-users', {
        pagination: { page: 1, perPage: 100 },
        sort: { field: 'user.name', order: 'ASC' },
        filter: {},
      });

      setUsers(response.data || []);
    } catch (error) {
      console.error('Error loading users:', error);
      notify(`Error loading users: ${error}`, { type: 'error' });
    } finally {
      setLoading(false);
    }
  };


  // Filter users based on search query
  const filteredUsers = users.filter((tenantUser) => {
    if (!searchQuery.trim()) return true;

    const query = searchQuery.toLowerCase();
    const name = tenantUser.user.name?.toLowerCase() || '';
    const email = tenantUser.user.email.toLowerCase();

    return name.includes(query) || email.includes(query);
  });

  const getRoleColor = (role: string): 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success' => {
    const roleColors: Record<string, 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success'> = {
      admin: 'error',
      operator: 'warning',
      user: 'info',
      super: 'secondary',
    };
    return roleColors[role] || 'default' as any;
  };


  if (!isAdmin) {
    return (
      <Box>
        <PageTitle
          title="Users"
          subtitle="Access denied"
          icon={<UsersIcon />}
        />
        <Container maxWidth="lg" sx={{ mt: 4 }}>
          <Typography>You don't have permission to manage users.</Typography>
        </Container>
      </Box>
    );
  }

  return (
    <Box>
      <PageTitle
        title="Users"
        subtitle="Manage users and their roles"
        icon={<UsersIcon />}
      />
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, gap: 2 }}>
          <TextField
            placeholder="Search by name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            sx={{ flexGrow: 1, maxWidth: 400 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/users/create')}
            sx={{ backgroundColor: '#1C5195' }}
          >
            Invite User
          </Button>
        </Box>

        <TableContainer component={Paper} sx={{ boxShadow: 1 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'rgba(28, 81, 149, 0.04)' }}>
                <TableCell>User</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Roles</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    Loading users...
                  </TableCell>
                </TableRow>
              ) : filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Box sx={{ py: 4 }}>
                      <Typography color="text.secondary" gutterBottom>
                        {users.length === 0 ? 'No users in this tenant yet' : 'No users match your search'}
                      </Typography>
                      {users.length === 0 && (
                        <Button
                          variant="outlined"
                          startIcon={<AddIcon />}
                          onClick={() => navigate('/users/create')}
                        >
                          Invite First User
                        </Button>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsers.map((tenantUser) => (
                  <TableRow
                    key={tenantUser.user.id}
                    hover
                    onClick={() => navigate(`/users/${tenantUser.user.id}`)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar
                          src={tenantUser.user.avatarUrl}
                          sx={{ width: 40, height: 40 }}
                        >
                          {tenantUser.user.name?.charAt(0) || tenantUser.user.email.charAt(0)}
                        </Avatar>
                        <Typography variant="body2" fontWeight="medium">
                          {tenantUser.user.name || 'Unnamed User'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <EmailIcon fontSize="small" color="action" />
                        {tenantUser.user.email}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={tenantUser.user.emailVerified ? 'Verified' : 'Pending'}
                        color={tenantUser.user.emailVerified ? 'success' : 'warning'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        {tenantUser.roles.map((role) => (
                          <Chip
                            key={role}
                            label={role}
                            size="small"
                            color={getRoleColor(role)}
                          />
                        ))}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Container>
    </Box>
  );
};